/**
 * Session Expiry Event Bus
 *
 * Lets the low-level fetch wrappers (authenticatedFetch.ts, apiClient.ts) signal
 * "the session is gone" (refresh failed / backend returned 401) up to App.tsx,
 * which owns isAuthenticated state and has no other way to hear from them —
 * there's no AuthContext and no router to redirect through.
 */

type SessionExpiredListener = () => void;

const listeners = new Set<SessionExpiredListener>();

/**
 * Subscribe to session-expired notifications. Returns an unsubscribe function.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Notify subscribers that the session is no longer valid (expired refresh token,
 * or a 401 from the backend that a refresh could not fix). Safe to call multiple
 * times in quick succession — subscribers are responsible for making repeat
 * notifications a no-op (e.g. logout is idempotent).
 */
export function emitSessionExpired(): void {
  for (const listener of listeners) {
    listener();
  }
}
